"use client";

import { AspectRatio } from "@/components/ui/aspect-ratio";
import Image from "next/image";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import Link from "next/link";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import {
  mediaObjectPositionClassName,
  type MediaObjectPosition,
} from "@/utilities/mediaObjectPosition";

type NewsSectionCarouselPost = {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  date: string;
  categories: string[];
  image: string;
  imageAlt: string;
  imageTitle?: string;
  imageObjectPosition: MediaObjectPosition;
};

type NewsSectionCarouselProps = {
  posts: NewsSectionCarouselPost[];
};

export function NewsSectionCarousel({ posts }: NewsSectionCarouselProps) {
  return (
    <div className="relative w-full">
      <Carousel
        opts={{
          align: "start",
          loop: false,
        }}
        className="w-full"
      >
        <div className="mb-4 flex gap-2 md:absolute md:right-0 md:-top-16 md:mb-0">
          <CarouselPrevious className="static top-0 bottom-0 h-10 w-10 translate-y-0" />
          <CarouselNext className="static top-0 bottom-0 h-10 w-10 translate-y-0" />
        </div>
        <CarouselContent className="items-stretch">
          {posts.map((post) => (
            <CarouselItem
              key={post.id}
              className="basis-9/10 md:basis-[47%] xl:basis-[31%]"
            >
              <Link href={`/news/${post.slug}`} className="group block">
                <Card className="flex cursor-pointer flex-col justify-between gap-6 rounded-none border-0 bg-transparent p-0 shadow-none ring-0">
                  <CardFooter className="border-0 p-0">
                    <AspectRatio
                      ratio={300 / 157}
                      className="w-full overflow-hidden rounded-xs md:rounded-sm"
                    >
                      {post.image ? (
                        <Image
                          src={post.image}
                          alt={post.imageAlt}
                          title={post.imageTitle ?? post.imageAlt}
                          fill
                          className={`h-full w-full object-cover transition-transform duration-200 group-hover:scale-105 ${mediaObjectPositionClassName(
                            post.imageObjectPosition,
                          )}`}
                        />
                      ) : null}
                    </AspectRatio>
                  </CardFooter>

                  <CardContent className="flex flex-col gap-3 p-0">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-left">
                      {post.date ? (
                        <span className="text-muted-foreground text-sm">
                          {post.date}
                        </span>
                      ) : null}
                      {post.categories.length ? (
                        <span className="text-muted-foreground text-sm">
                          {post.date ? "• " : ""}
                          {post.categories.join(", ")}
                        </span>
                      ) : null}
                    </div>

                    <h3 className="text-xl leading-normal font-semibold hover:underline">
                      {post.title}
                    </h3>

                    <p className="text-muted-foreground line-clamp-2 text-base leading-normal">
                      {post.excerpt}
                    </p>
                  </CardContent>
                </Card>
              </Link>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
    </div>
  );
}
